'use client';

import { useState } from 'react';
import { User, Star, Calendar, Target } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, getInitials } from '@/lib/utils';
import { usePlayerDetail, usePlayerTrends } from '@/hooks/use-coach-data';
import { OverviewTab } from '@/components/coach/player-detail/overview-tab';
import { EvaluationsTab } from '@/components/coach/player-detail/evaluations-tab';
import { AttendanceTab } from '@/components/coach/player-detail/attendance-tab';
import { PlayerGoals } from '@/components/coach/player-goals';
import type { Player } from '@/shared/types/coach.types';

interface PlayerDetailModalProps {
  player: Player | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewEvaluation?: () => void;
}

type TabId = 'overview' | 'evaluations' | 'attendance' | 'goals';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'evaluations', label: 'Evaluations', icon: Star },
  { id: 'attendance', label: 'Attendance', icon: Calendar },
  { id: 'goals', label: 'Goals', icon: Target },
];

export function PlayerDetailModal({
  player,
  open,
  onOpenChange,
  onNewEvaluation,
}: PlayerDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const { data, isLoading } = usePlayerDetail(player?.id ?? null);
  const { data: trends } = usePlayerTrends(player?.id ?? '');

  const handleClose = () => {
    setActiveTab('overview');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Player Details</DialogTitle>
        </DialogHeader>

        {player && (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Player Header */}
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-lg">
                {getInitials(player.firstName, player.lastName)}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground">
                  {player.firstName} {player.lastName}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  {player.skillLevel && (
                    <Badge variant="outline" className="capitalize">
                      {player.skillLevel}
                    </Badge>
                  )}
                  {player.utrRating && (
                    <Badge variant="secondary" className="text-green-700 bg-green-100">
                      UTR: {player.utrRating}
                    </Badge>
                  )}
                  {data?.player.age && (
                    <span className="text-sm text-muted-foreground">
                      {data.player.age} years old
                    </span>
                  )}
                </div>
              </div>
              {onNewEvaluation && (
                <Button onClick={onNewEvaluation} size="sm">
                  New Evaluation
                </Button>
              )}
            </div>

            {/* Tabs */}
            <div className="border-b flex-shrink-0 mt-4">
              <div className="flex gap-1">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 border-b-2 transition text-sm',
                      activeTab === tab.id
                        ? 'border-primary text-primary font-medium'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto py-4">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : data ? (
                <>
                  {activeTab === 'overview' && (
                    <OverviewTab data={data} trends={trends ?? null} />
                  )}
                  {activeTab === 'evaluations' && (
                    <EvaluationsTab evaluations={data.evaluations} stats={data.stats} />
                  )}
                  {activeTab === 'attendance' && (
                    <AttendanceTab attendance={data.attendance} stats={data.stats} />
                  )}
                  {activeTab === 'goals' && player && (
                    <PlayerGoals playerId={player.id} />
                  )}
                </>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Failed to load player details
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
